import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Palette, Sparkles, Zap, Timer, Trophy } from "lucide-react";

// Custom CSS for specific glow effects and animations
const customStyles = `
  .text-glow {
    text-shadow: 0 0 10px rgba(139, 92, 246, 0.5), 0 0 20px rgba(139, 92, 246, 0.3);
  }
  .box-glow {
    box-shadow: 0 0 15px rgba(139, 92, 246, 0.3);
  }
  .electric-border {
    position: relative;
    overflow: hidden;
  }
  .electric-border::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    animation: shine 3s infinite;
  }
  @keyframes shine {
    0% { left: -100%; }
    20% { left: 100%; }
    100% { left: 100%; }
  }
`;

const ColorPlay = () => {
  const [currentGradient, setCurrentGradient] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [gameMode, setGameMode] = useState<'practice' | 'challenge'>('practice');
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [targetColor, setTargetColor] = useState('');
  const [countdown, setCountdown] = useState(3);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [feedback, setFeedback] = useState<'hit' | 'miss' | null>(null);

  const gradients = [
    "from-purple-400 via-pink-500 to-red-500",
    "from-blue-400 via-indigo-500 to-purple-600",
    "from-emerald-400 via-teal-500 to-cyan-500",
    "from-yellow-400 via-orange-500 to-red-600",
    "from-cyan-400 via-blue-500 to-indigo-600",
    "from-rose-400 via-fuchsia-500 to-indigo-600",
  ];

  const startGame = (mode: 'practice' | 'challenge' = 'practice') => {
    setGameMode(mode);
    setScore(0);
    setGameActive(false);
    setGameEnded(false);
    setFeedback(null);

    if (mode === 'challenge') {
      setIsCountingDown(true);
      setCountdown(3);
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setIsCountingDown(false);
            setGameActive(true);
            setTimeLeft(30);
            generateTargetColor();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setGameActive(true);
    }
  };

  const generateTargetColor = () => {
    const randomIndex = Math.floor(Math.random() * gradients.length);
    setTargetColor(gradients[randomIndex]);
    // Also set the display to the target so the user knows what to look for (or hide it for harder difficulty)
    setCurrentGradient(randomIndex); 
  };

  const checkColorMatch = (selectedIndex: number) => {
    if (gradients[selectedIndex] === targetColor) {
      setScore(prev => prev + 10);
      setFeedback('hit');
      setTimeout(() => {
        setFeedback(null);
        generateTargetColor();
      }, 500);
    } else {
        setScore(prev => Math.max(0, prev - 5));
        setFeedback('miss');
        setTimeout(() => setFeedback(null), 500);
    }
  };

  const nextGradient = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentGradient((prev) => (prev + 1) % gradients.length);
      setIsAnimating(false);
    }, 500);
  };

  useEffect(() => {
    if (!gameActive || gameMode !== 'challenge') return;
    const timerInterval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameActive(false);
          setGameEnded(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerInterval);
  }, [gameActive, gameMode]);

  useEffect(() => {
    if (!gameActive && gameMode === 'practice') {
      const interval = setInterval(nextGradient, 3000);
      return () => clearInterval(interval);
    }
  }, [gameActive, gameMode]);

  return (
    <main className="min-h-screen bg-slate-950 text-white selection:bg-purple-500 selection:text-white font-sans overflow-hidden relative">
      <style>{customStyles}</style>
      
      {/* Background Ambience */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay pointer-events-none" />
      
      <div className="container mx-auto px-4 py-12 relative z-10 max-w-5xl">
        
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-2 px-4 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 backdrop-blur-md">
            <Zap className="w-4 h-4 text-purple-400 fill-purple-400" />
            <span className="text-xs font-bold tracking-widest uppercase text-purple-300">System Ready</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black mb-4 tracking-tight">
            THUNDER <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 text-glow">STORM</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto font-light">
            Master the spectrum. Engage in high-velocity color matching or relax in the ambient flow.
          </p>
        </motion.div>

        {/* Game State Displays (Countdown / Game Over) */}
        <AnimatePresence mode="wait">
            {isCountingDown && (
                <motion.div 
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 1.5, opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
                >
                    <div className="text-9xl font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-purple-600 text-glow">
                        {countdown}
                    </div>
                </motion.div>
            )}

            {gameEnded && (
                <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-8 p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl text-center max-w-md mx-auto relative overflow-hidden group"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <Trophy className="w-16 h-16 mx-auto mb-4 text-yellow-400 drop-shadow-lg" />
                    <h3 className="text-3xl font-bold mb-2 text-white">Simulation Complete</h3>
                    <p className="text-slate-400 mb-6">Final Efficiency Score</p>
                    <div className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mb-8">
                        {score}
                    </div>
                    <button 
                        onClick={() => startGame('challenge')}
                        className="px-8 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold shadow-[0_0_20px_rgba(147,51,234,0.5)] transition-all hover:scale-105"
                    >
                        Reboot System
                    </button>
                </motion.div>
            )}
        </AnimatePresence>

        {/* Mode Selectors */}
        {!gameActive && !gameEnded && !isCountingDown && (
             <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col sm:flex-row justify-center gap-6 mb-16"
            >
                <button
                    onClick={() => startGame('practice')}
                    className="group relative px-8 py-4 bg-slate-900 rounded-xl overflow-hidden border border-slate-700 hover:border-purple-500/50 transition-colors"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-3 relative z-10">
                        <Palette className="w-5 h-5 text-purple-400" />
                        <span className="font-bold text-lg">Ambient Mode</span>
                    </div>
                </button>
                
                <button
                    onClick={() => startGame('challenge')}
                    className="group relative px-8 py-4 bg-slate-900 rounded-xl overflow-hidden border border-slate-700 hover:border-cyan-500/50 transition-colors"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-3 relative z-10">
                        <Zap className="w-5 h-5 text-cyan-400" />
                        <span className="font-bold text-lg">Speed Run</span>
                    </div>
                </button>
             </motion.div>
        )}

        {/* Main Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Left Panel: Statistics & Feedback */}
            {gameActive && gameMode === 'challenge' && (
                <motion.div 
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    className="lg:col-span-3 space-y-4"
                >
                    <div className="bg-slate-900/80 backdrop-blur-md p-6 rounded-2xl border border-slate-800 electric-border">
                        <div className="flex items-center gap-3 mb-2 text-slate-400">
                            <Timer className="w-4 h-4" />
                            <span className="text-xs font-bold uppercase tracking-wider">Time Remaining</span>
                        </div>
                        <div className="text-4xl font-mono font-bold text-white">
                            00:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                        </div>
                    </div>

                    <div className="bg-slate-900/80 backdrop-blur-md p-6 rounded-2xl border border-slate-800">
                        <div className="flex items-center gap-3 mb-2 text-slate-400">
                            <Trophy className="w-4 h-4" />
                            <span className="text-xs font-bold uppercase tracking-wider">Current Score</span>
                        </div>
                        <div className="text-4xl font-mono font-bold text-cyan-400">
                            {score}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Center Panel: The Main Viewport */}
            <div className={`${gameActive && gameMode === 'challenge' ? 'lg:col-span-9' : 'lg:col-span-12'}`}>
                
                <motion.div
                    className="relative w-full aspect-[2/1] rounded-3xl overflow-hidden shadow-2xl mb-8 group"
                    style={{ perspective: "1000px" }}
                >
                    {/* The Gradient Display */}
                    <motion.div
                        key={currentGradient}
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className={`absolute inset-0 bg-gradient-to-br ${gradients[currentGradient]}`}
                    />
                    
                    {/* Glass Overlay/HUD */}
                    <div className="absolute inset-0 bg-white/5 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                         {gameMode === 'challenge' && gameActive ? (
                            <motion.div 
                                animate={feedback === 'hit' ? { scale: [1, 1.2, 1] } : feedback === 'miss' ? { x: [-10, 10, -10, 10, 0] } : {}}
                                className="bg-black/30 backdrop-blur-xl p-6 rounded-2xl border border-white/10 text-center"
                            >
                                <h2 className="text-2xl font-bold mb-1">
                                    {feedback === 'hit' ? 'MATCH CONFIRMED' : feedback === 'miss' ? 'ERROR: MISMATCH' : 'TARGET ACQUIRED'}
                                </h2>
                                <p className="text-white/70 text-sm font-mono">
                                    Identify this gradient pattern below
                                </p>
                            </motion.div>
                         ) : (
                            <div className="bg-black/20 backdrop-blur-sm px-6 py-2 rounded-full border border-white/10">
                                <span className="text-sm font-mono text-white/80">PREVIEW MODE // {gradients[currentGradient].split(' ')[1].replace('from-', '')} spectrum</span>
                            </div>
                         )}
                    </div>
                </motion.div>

                {/* Control Deck (The Buttons) */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {gradients.map((grad, index) => (
                        <motion.button
                            key={index}
                            layout
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                if (gameMode === 'challenge' && gameActive) {
                                    checkColorMatch(index);
                                } else {
                                    setCurrentGradient(index);
                                }
                            }}
                            className={`
                                relative h-24 rounded-xl overflow-hidden border border-slate-700 group transition-all
                                ${gameMode === 'challenge' && gameActive ? 'hover:border-white cursor-crosshair' : 'hover:border-purple-500 cursor-pointer'}
                            `}
                        >
                            <div className={`absolute inset-0 bg-gradient-to-r ${grad} opacity-80 group-hover:opacity-100 transition-opacity`} />
                            
                            {/* Hover Reveal Effect */}
                            <div className="absolute inset-0 bg-black/40 group-hover:bg-transparent transition-colors duration-300 flex items-center justify-center">
                                {gameMode === 'challenge' && gameActive ? (
                                    <Zap className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-all scale-50 group-hover:scale-100 drop-shadow-md" />
                                ) : (
                                    <Sparkles className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-all scale-50 group-hover:scale-100 drop-shadow-md" />
                                )}
                            </div>
                            
                            {/* Active Indicator */}
                            {currentGradient === index && gameMode !== 'challenge' && (
                                <div className="absolute bottom-2 right-2 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white] animate-pulse" />
                            )}
                        </motion.button>
                    ))}
                </div>

            </div>
        </div>

        {/* Footer Ambient Elements */}
        <div className="mt-20 flex justify-center gap-4 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
            {["bg-red-500", "bg-blue-500", "bg-green-500", "bg-yellow-500"].map((color, i) => (
                <div key={i} className={`${color} w-2 h-12 rounded-full blur-md animate-pulse`} style={{ animationDelay: `${i * 0.2}s`}} />
            ))}
        </div>

      </div>
    </main>
  );
};

export default ColorPlay;